import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ExternalLink, IdCard } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { useLang } from "../lib/i18n";
import { usePublicConfig } from "../lib/config";
import { CardView } from "../components/card/CardView";
import { copyCardLink } from "../components/card/QrDialog";
import { useAuth, can } from "../lib/auth";
import { PhotoUploader } from "./my-card/PhotoUploader";
import { ProfileForm } from "./my-card/ProfileForm";
import { ThemePicker } from "./my-card/ThemePicker";
import { WalletPanel } from "./my-card/WalletPanel";

const SharePanel = ({ card }) => {
  const { t } = useLang();
  return (
    <div className="rounded-3xl border border-slate-200/80 bg-white p-5" data-testid="share-panel">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("share")}</p>
      <p dir="ltr" className="mt-2 break-all text-start font-mono text-sm text-slate-800" data-testid="share-card-url">{card.card_url}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={() => copyCardLink(card.card_url, t)} data-testid="share-copy-link"
          className="btn-press inline-flex min-h-[46px] items-center gap-2 rounded-xl border border-slate-300 px-4 text-sm font-semibold hover:border-slate-900">
          <Copy className="h-4 w-4" /> {t("copy_link")}
        </button>
        <Link to={`/c/${card.slug}`} target="_blank" data-testid="share-open-card"
          className="btn-press inline-flex min-h-[46px] items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white">
          <ExternalLink className="h-4 w-4" /> {t("open_card")}
        </Link>
      </div>
    </div>
  );
};

export default function MyCard() {
  const { t } = useLang();
  const config = usePublicConfig();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/card").then(({ data }) => setData(data)).catch((e) => toast.error(errorMessage(e)));
  }, []);

  if (!data) return <div className="h-96 animate-pulse rounded-3xl bg-slate-200" data-testid="my-card-loading" />;
  if (!data.employee) {
    return (
      <div className="max-w-lg pt-10" data-testid="my-card-empty">
        <IdCard className="h-10 w-10 text-slate-400" />
        <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-slate-900">{t("no_card_title")}</h1>
        <p className="mt-3 text-slate-600">{t("no_card_body")}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-10 lg:grid-cols-12" data-testid="my-card-page">
      <div className="space-y-6 lg:col-span-7">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{t("edit_details")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("edit_hint")}</p>
        </div>
        {can(user, "cards.upload_photo") && <PhotoUploader employee={data.employee} onChange={setData} />}
        {can(user, "cards.change_theme") && <ThemePicker current={data.employee.theme} useImage={data.employee.bg_image} imageUrl={config?.profile_design?.bg_image_url} themes={config?.themes} onChange={setData} />}
        <ProfileForm employee={data.employee} fields={data.fields} canEdit={can(user, "cards.edit_own")} onSaved={setData} />
        <SharePanel card={data.card} />
        {config?.wallet_enabled && (
          <WalletPanel employee={data.employee}
            onDownloaded={() => setTimeout(() => api.get("/me/card").then(({ data }) => setData(data)), 2500)} />
        )}
      </div>
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-24"><CardView card={data.card} config={config} /></div>
      </div>
    </div>
  );
}
